/*
 * Emotion styled 컴포넌트를 Tailwind className 문자열로 옮긴 모듈.
 * 유틸리티로 표현할 수 없는 규칙(가상요소 nth-of-type, 다중 duration transition,
 * env() safe-area, 서드파티 iframe 자식 선택자, 인라인 스타일로 덮어써야 하는 CSS 변수)은
 * globals.css 의 semantic class 로 남기고 여기서는 그 클래스 이름만 붙인다.
 */

export const showcaseContainer = 'mt-16 lg:mt-[clamp(72px,6.667vw,96px)]';

/*
 * --defect-progress-percent 등 4개 커스텀 프로퍼티는 useDefectProgress 가
 * section.style.setProperty() 로 갱신한다. Tailwind 유틸리티는 !important 라
 * 인라인 스타일을 이겨버리므로 기본값은 반드시 globals.css(.industrial-sample-section)에 둔다.
 */
export const sampleSection =
  'industrial-sample-section py-10 first-of-type:mt-9 lg:grid lg:grid-cols-[minmax(220px,0.36fr)_minmax(0,0.64fr)] lg:items-stretch lg:gap-x-[clamp(48px,6vw,96px)] lg:py-14';

export const sectionHeaderColumn = 'lg:self-stretch lg:h-full lg:min-h-full';

export const sectionHeader =
  'grid content-start gap-2.5 lg:sticky lg:top-[120px] lg:self-start lg:h-max lg:z-[1]';

export const sectionEyebrow = 'text-sm font-bold leading-[140%] text-[#ff3d59]';

export const sectionTitle =
  'text-[24px] font-bold leading-[135%] tracking-[0] text-balance text-apps-coreGrayWhite lg:text-[32px] lg:leading-[130%]';

export const sectionDescription =
  'text-[15px] font-medium leading-[155%] text-apps-brandGray600';

/*
 * lg 에서도 gap 은 18px 로 동일해 중복 선언을 생략했다.
 * 원본에 line-height 선언이 없어 text-sm(= line-height 동반) 대신 text-[14px] 를 쓴다.
 */
export const verticalDefectScale =
  'relative grid gap-[18px] mt-9 text-[14px] font-bold text-apps-brandGray600 lg:mt-8';

export const defectScaleSummary =
  'grid grid-cols-[auto_1fr] items-end gap-x-[14px] gap-y-2 lg:grid-cols-[1fr_auto] lg:items-baseline lg:gap-x-[18px]';

/*
 * 원본은 &[data-defect-label] 로 색을 덮었다. 같은 color 속성을 두 유틸리티가 다투지 않도록
 * 호출부에서 넘긴 플래그로 승자를 JS 에서 계산해 하나만 내보낸다.
 */
export function verticalScaleLabel(isDefectLabel = false) {
  const color = isDefectLabel ? 'text-[#ff3d59]' : 'text-apps-brandGray600';
  return `inline-flex text-sm font-bold leading-[140%] ${color} lg:text-[15px] lg:col-[1/-1]`;
}

export const defectMeterStage =
  'block text-[56px] font-bold leading-[86%] tracking-[0] text-[rgba(255,255,255,0.88)] tabular-nums lg:text-[clamp(72px,6vw,96px)]';

export const verticalScaleBody =
  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-[14px] lg:grid-cols-[clamp(88px,7vw,116px)_minmax(0,1fr)] lg:items-stretch lg:gap-[22px] lg:min-h-[200px]';

/*
 * 원본 lg 블록의 `background: linear-gradient(...)` 은 shorthand 라 기본 background-color 도
 * 지운다. Tailwind 의 bg-[<gradient>] 는 background-image 만 건드리므로 lg:bg-transparent 로
 * background-color 를 명시적으로 되돌려 원본과 같은 결과를 만든다.
 */
export const verticalScaleTrack =
  'relative h-[5px] overflow-hidden rounded-[999px] bg-[rgba(255,255,255,0.18)] lg:w-full lg:h-full lg:bg-transparent lg:bg-[linear-gradient(180deg,rgba(255,255,255,0.2),rgba(255,255,255,0.08))] lg:shadow-[inset_0_0_0_1px_rgba(255,255,255,0.1),0_22px_48px_rgba(255,61,89,var(--defect-track-shadow-alpha))]';

export const verticalScaleFill =
  'absolute top-0 left-0 w-[var(--defect-progress-percent,0%)] h-full bg-[linear-gradient(90deg,rgba(255,255,255,0.58),#ff3d59)] lg:w-full lg:h-[var(--defect-progress-percent,0%)] lg:bg-[linear-gradient(180deg,rgba(255,255,255,0.46),rgba(255,61,89,0.9)_58%,#ff3d59)] lg:shadow-[0_0_calc(28px_*_var(--defect-glow-opacity))_rgba(255,61,89,var(--defect-shadow-alpha))]';

export const verticalScaleMarks =
  'flex justify-between gap-2.5 min-w-[120px] lg:grid lg:content-between lg:gap-0 lg:min-w-auto';

/*
 * ::before 의 nth-of-type 크기 단계, [data-active] 상태, 자식 span 타이포까지 얽혀 있어
 * 유틸리티로 쪼개면 오히려 깨지기 쉬워 globals.css 에 통째로 남긴다.
 */
export const verticalScaleMark = 'industrial-defect-mark';

export const defectScaleFooter = 'grid items-start';

export const sampleList = 'grid mt-[18px] lg:col-2 lg:mt-6';

export const sampleRow =
  'grid gap-[18px] py-6 lg:[[data-motor-sample-section]_&]:py-[clamp(28px,3vw,42px)]';

export const sampleMeta =
  'grid grid-cols-[42px_minmax(0,1fr)_auto] items-start gap-4 [@media(max-width:480px)]:grid-cols-[36px_minmax(0,1fr)]';

export const sampleIndex =
  'text-xl font-bold leading-[120%] text-[rgba(255,255,255,0.72)]';

export const sampleText = 'grid gap-1.5';

export const sampleTitle =
  'text-lg font-bold leading-[140%] tracking-[0] text-pretty text-apps-coreGrayWhite lg:text-xl';

export const sampleDescription =
  'text-[15px] font-medium leading-[150%] text-apps-brandGray600';

export const sampleLevel =
  'text-[13px] font-bold leading-[140%] text-[#ff3d59] [@media(max-width:480px)]:col-2';

export const mobileStageMeter =
  'grid gap-2 ml-[58px] lg:hidden [@media(max-width:480px)]:ml-[52px]';

export const mobileStageTrack =
  'h-1 overflow-hidden rounded-[999px] bg-[rgba(255,255,255,0.16)]';

export const mobileStageFill =
  'h-full rounded-[inherit] bg-[linear-gradient(90deg,rgba(255,255,255,0.5),#ff3d59)] shadow-[0_0_18px_rgba(255,61,89,0.28)]';

export const mobileStageLabels =
  'flex justify-between text-xs font-bold leading-[140%] text-apps-brandGray600 [&_span:last-of-type]:text-[#ff3d59]';

export const demoFrame =
  'grid aspect-video mt-6 overflow-hidden border border-[rgba(255,255,255,0.14)] rounded-lg bg-[rgba(255,255,255,0.04)] lg:col-2 lg:mt-0';

export const demoVideoStage = 'relative w-full h-full';

export const demoVideoOpenButton =
  'absolute inset-0 z-[2] w-full h-full border-0 rounded-[inherit] bg-transparent cursor-zoom-in touch-manipulation data-[state=focused]:pointer-events-none data-[state=focused]:invisible data-[state=closing]:pointer-events-none data-[state=closing]:invisible focus-visible:outline-0 focus-visible:shadow-[inset_0_0_0_3px_rgba(255,61,89,0.72)]';

/*
 * framer-motion 의 layout 애니메이션이 inline style 로 transform 을 쓴다.
 * Tailwind 유틸리티는 !important 라 inline transform 을 덮어써 버리므로
 * hover/active scale 과 두 가지 duration 을 쓰는 transition 은 CSS 로 남긴다.
 */
export const demoPreviewFrame = 'industrial-demo-preview-frame';

export const demoPreviewFallbackVideo =
  'block w-full h-full bg-black object-cover';

/*
 * IndustrialDemoVideoFocusFrame 이 닫힐 때 inline style 로 top/left/width/height/
 * max-height/transform-origin/translate 를 덮어쓴다. 유틸리티(!important)로 만들면
 * 그 인라인 값이 무시되므로 전부 CSS 에 남긴다.
 */
export const videoFocusFrame = 'industrial-video-focus-frame';

export const videoFocusVideo = 'block w-full h-full bg-black object-contain';

// > div, iframe 은 YouTube iframe API 가 만든 서드파티 DOM 이라 CSS 로 남긴다.
export const youTubePlayerFrame =
  'industrial-youtube-frame absolute inset-0 w-full h-full bg-black';

// visibility 만 지연시키는 transition(0s linear 160ms)은 유틸리티로 표현할 수 없다.
export const videoFocusLayer = 'industrial-video-focus-layer';

export const videoFocusBackdrop =
  'absolute inset-0 w-full h-full border-0 bg-[rgba(0,0,0,0.74)] cursor-zoom-out touch-manipulation';

// env(safe-area-inset-*) + 다중 timing transition 이라 CSS 로 남긴다.
export const videoFocusCloseButton = 'industrial-video-close-button';

export const demoPlaceholder =
  'grid place-items-center text-[15px] font-bold text-apps-brandGray600';

export const videoPlayer = 'w-full h-full object-cover';
